import { Link, useParams } from "react-router-dom";
import MovieGrid from "@/components/feature/MovieGrid";
import EmptyState from "@/components/feature/EmptyState";
import SectionHeader from "@/components/feature/SectionHeader";
import { usePageMeta } from "@/hooks/usePageMeta";
import { specials } from "@/mocks/specials";
import { getMoviesByIds } from "@/utils/content";

export default function Special() {
  const { id } = useParams();
  const special = specials.find((item) => item.id === id);

  usePageMeta({
    title: special
      ? `${special.title} - 秘密影院影视专题`
      : "影视专题 - 秘密影院",
    description: special
      ? `${special.intro.slice(0, 80)}…… 秘密影院为你整理的「${special.title}」专题合集，一次看个够。`
      : "秘密影院精心策划的影视专题合集，帮你按主题发现更多好片。",
    keywords: special
      ? `${special.title},影视专题,秘密影院,专题推荐,影视合集`
      : "影视专题,秘密影院,专题推荐"
  });

  if (!special) {
    return (
      <div className="w-full px-4 py-20 md:px-6">
        <EmptyState
          icon="ri-compasses-2-line"
          title="这个专题暂时不存在"
          description="你访问的专题可能已下线或链接有误，去看看其他精彩专题吧。"
          action={
            <Link
              to="/"
              className="cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-700 text-background-950 transition-colors hover:bg-accent-600"
            >
              返回首页
            </Link>
          }
        />
      </div>
    );
  }

  const related = specials.filter((item) => special.relatedIds.includes(item.id));

  return (
    <div>
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={special.cover}
            alt={`${special.title} 专题封面`}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/80 to-background-950/50" />
        </div>
        <div className="relative w-full px-4 pb-12 pt-14 md:px-6 md:pb-16 md:pt-20">
          <nav className="mb-5 flex items-center gap-1 text-sm text-foreground-500" aria-label="面包屑">
            <Link to="/" className="cursor-pointer transition-colors hover:text-accent-400">
              首页
            </Link>
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-right-s-line" aria-hidden="true" />
            </span>
            <span className="text-foreground-200">专题</span>
          </nav>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-500/20 px-3 py-1 text-xs font-500 text-secondary-300">
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-fire-line" aria-hidden="true" />
            </span>
            影视专题
          </span>
          <h1 className="mt-4 max-w-3xl font-heading text-3xl font-800 leading-tight text-foreground-50 md:text-5xl">
            {special.title}
          </h1>
          <p className="mt-3 text-base text-accent-300 md:text-lg">{special.subtitle}</p>
          <p className="mt-5 max-w-3xl text-sm leading-7 text-foreground-300 md:text-base">
            {special.intro}
          </p>
        </div>
      </section>

      <div className="w-full px-4 py-10 md:px-6 md:py-12">
        <div className="mb-10 flex items-start gap-3 rounded-lg border border-secondary-500/30 bg-secondary-500/10 p-5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-500/20 text-secondary-300">
            <i className="ri-lightbulb-line text-lg" aria-hidden="true" />
          </span>
          <div>
            <h2 className="font-heading text-sm font-600 text-secondary-200">
              推荐理由
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-foreground-300">
              {special.reason}
            </p>
          </div>
        </div>

        <div className="space-y-12">
          {special.groups.map((group) => {
            const groupMovies = getMoviesByIds(group.movieIds);
            return (
              <section key={group.name}>
                <SectionHeader title={group.name} subtitle={group.description} />
                <MovieGrid movies={groupMovies} />
              </section>
            );
          })}
        </div>

        {related.length > 0 && (
          <section className="mt-16">
            <SectionHeader title="相关专题" subtitle="换个主题，继续探索更多好片" />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/special/${item.id}`}
                  className="group relative block aspect-[16/10] cursor-pointer overflow-hidden rounded-lg border border-background-800"
                >
                  <img
                    src={item.cover}
                    alt={`${item.title} 专题封面`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/50 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-4">
                    <h3 className="font-heading text-base font-600 text-foreground-50 transition-colors group-hover:text-accent-400">
                      {item.title}
                    </h3>
                    <p className="mt-1 line-clamp-1 text-xs text-foreground-300">
                      {item.subtitle}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}